import { Link } from 'react-router-dom';
import { Monitor, Brain, Zap, Shield, Globe, Package, HardDrive, Key, BarChart3, Database, Clock, Terminal } from 'lucide-react';
import { SEOHead } from '@/components/seo';
import { PAGE_SEO } from '@/lib/seo-config';

const featureBlocks = [
  {
    title: 'RDP Access', icon: Monitor,
    desc: 'Full desktop control via browser-based RDP client. Configure your OpenClaw instance with a familiar GUI.',
    details: ['Browser-based Guacamole client', 'Native RDP file download', 'IP whitelisting', 'Session recording'],
  },
  {
    title: 'Multi-Agent Support', icon: Brain,
    desc: 'Run multiple OpenClaw agents on a single instance. Each with their own personality and skills.',
    details: ['Up to 20 agents per instance', 'Independent configurations', 'Shared resource pool', 'Per-agent analytics'],
  },
  {
    title: '60-Second Deploy', icon: Zap,
    desc: 'From signup to a running agent in under a minute. Pre-configured VPS with OpenClaw pre-installed.',
    details: ['One-click provisioning', 'Pre-configured messaging', 'Auto SSL/TLS setup', 'Instant DNS allocation'],
  },
  {
    title: 'Enterprise Security', icon: Shield,
    desc: 'SOC 2 preparation, encrypted backups, firewall rules, and DDoS protection on every instance.',
    details: ['AES-256 encryption at rest', 'TLS 1.3 in transit', 'Automated security patches', 'Intrusion prevention'],
  },
  {
    title: '8 Global Regions', icon: Globe,
    desc: 'Deploy close to your users for minimal latency. US, EU, Asia-Pacific, and more.',
    details: ['us-east-1, us-west-2', 'eu-west-1, eu-central-1', 'ap-south-1, ap-northeast-1', 'sa-east-1, af-south-1'],
  },
  {
    title: 'Skills Marketplace', icon: Package,
    desc: 'Browse and install from 100+ community-built skills. Gmail, GitHub, Notion, Spotify, and more.',
    details: ['One-click install', 'Dependency management', 'Custom skill upload', 'Verified & community skills'],
  },
  {
    title: 'Pre-configured Templates', icon: HardDrive,
    desc: 'Start with a template optimized for your use case. Personal assistant, support agent, dev bot, and more.',
    details: ['Personal productivity', 'Customer support', 'Developer assistant', 'Agency multi-client'],
  },
  {
    title: 'API Key Management', icon: Key,
    desc: 'Securely store and manage your LLM API keys. Switch between providers with one click.',
    details: ['Anthropic, OpenAI, Google', 'OpenRouter, Local models', 'Encrypted storage', 'Usage quotas & fallback'],
  },
  {
    title: 'Cost Monitoring', icon: BarChart3,
    desc: 'Real-time dashboard showing LLM API spend, resource usage, and projected costs.',
    details: ['Per-instance breakdown', 'Token usage tracking', 'Budget alerts', 'Cost optimization tips'],
  },
  {
    title: 'Automated Backups', icon: Database,
    desc: 'Daily automated backups with configurable retention. One-click restore at any time.',
    details: ['Daily snapshots', 'Multi-region storage', 'Point-in-time recovery', '30-day retention'],
  },
  {
    title: 'Scheduled Operations', icon: Clock,
    desc: 'Schedule your agents to start and stop automatically. Save costs during off-hours.',
    details: ['Cron-based scheduling', 'Timezone aware', 'Cost savings calculator', 'Holiday calendars'],
  },
  {
    title: 'SSH & Web Console', icon: Terminal,
    desc: 'Full terminal access for power users. Browse files, edit configs, and run commands from your browser.',
    details: ['Browser-based terminal', 'SFTP file transfer', 'Code editor', 'Process manager'],
  },
];

export default function Features() {
  return (
    <div className="bg-[#050505] min-h-screen pt-24">
      <SEOHead
        title={PAGE_SEO.features.title}
        description={PAGE_SEO.features.description}
        keywords={PAGE_SEO.features.keywords}
        url="/features"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Features', url: '/features' },
        ]}
      />
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-20">
            <div className="inline-block mb-4 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-sm">
              <span className="text-xs font-mono text-emerald-400 uppercase tracking-widest">Features</span>
            </div>
            <h1 data-testid="features-page-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight section-marker">
              Everything You Need
            </h1>
            <p className="text-base md:text-lg text-zinc-400 leading-relaxed">
              Managed OpenClaw hosting with RDP access, multi-agent support, and enterprise-grade infrastructure.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featureBlocks.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  data-testid={`feature-block-${i}`}
                  className="bg-gradient-to-b from-white/[0.03] to-transparent border border-white/5 rounded-sm p-8 hover:border-emerald-500/20 transition-all group"
                >
                  <Icon size={28} className="text-emerald-400 mb-5 group-hover:scale-110 transition-transform" />
                  <h3 className="text-white font-mono font-bold text-lg mb-2">{f.title}</h3>
                  <p className="text-zinc-500 text-sm leading-relaxed mb-5">{f.desc}</p>
                  <ul className="space-y-2">
                    {f.details.map((d, j) => (
                      <li key={j} className="text-xs font-mono text-zinc-600 flex items-center gap-2">
                        <span className="text-emerald-500/50">&#x2022;</span> {d}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="mt-20 text-center">
            <Link
              to="/signup"
              data-testid="features-cta"
              className="inline-flex items-center gap-2 bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm px-8 py-3 hover:bg-emerald-400 transition-all click-scale shadow-[0_0_15px_rgba(16,185,129,0.3)]"
            >
              Start 14-Day Free Trial
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
